import { computed, inject, Injectable, signal } from '@angular/core';
import { SidebarOption } from '@utils/interfaces/sidebar.interface';
import { SessionService } from '@utils/services/session.service';

@Injectable({
  providedIn: 'root',
})
export class SideBarService {
  private _isOpen = signal<boolean>(false);
  public isOpen = computed(() => this._isOpen());
  public readonly toggleAside = signal<boolean>(false);
  private readonly _openSection = signal<string | null>(null);
  public readonly openSection = this._openSection.asReadonly();
  private readonly _sessionService = inject(SessionService);

  public sidebarOptions = computed(() => {
    return this.setSidebarOptions();
  });

  public setOpen(): void {
    this._isOpen.set(true);
    this.toggleAside.set(true);
  }

  public setClosed(): void {
    this._isOpen.set(false);
    this.toggleAside.set(false);
  }

  public toggle(): void {
    this._isOpen.set(!this._isOpen());
    this.toggleAside.set(!this.toggleAside());
  }

  public toggleSection(sectionId: string): void {
    this._openSection.update((current) =>
      current === sectionId ? null : sectionId,
    );
  }

  public clearOptions(): void {
    this._openSection.set(null);
  }

  public deleteSubOption(sidebarOptions: SidebarOption[], idOption: string[], idSubOption: string[]) {
    return sidebarOptions.map(option =>
      idOption.includes(option.id) && option.subOptions
        ? {
          ...option,
          subOptions: option.subOptions.filter(sub => !idSubOption.includes(sub.id))
        }
        : option
    );
  }

  setSidebarOptions(): SidebarOption[] {
    const user = this._sessionService.user;
    if (user?.has_collection_house) {
      return [
        {
          id: 'collection-house',
          label: 'Casa de cobranza',
          module: 'collection_house',
          icon: 'building',
          subOptions: [
            {
              id: 'collection-house-current',
              label: 'Procesos de cartera',
              linkTo: "/priv/casa-cobranza",
              params: { global_status: 'current' },
              module: 'collection_house',
              icon: 'folder-open'
            },
            {
              id: 'payment-review',
              label: 'Historial de pagos',
              linkTo: '/priv/historial-pago',
              params: { reviewed: 'false' },
              module: 'collection_process',
              icon: 'receipt'
            },
            {
              id: 'payment-links',
              label: 'Historial de enlaces de pago',
              linkTo: '/priv/procesos-cobranza/enlaces-de-pago',
              module: 'collection_process',
              icon: 'credit-card'
            },
            {
              id: 'restructuring-process-list',
              label: 'Restructuraciones',
              linkTo: '/priv/restructuraciones',
              module: 'restructuring_process',
              icon: 'list',
              visible: true,
            },
          ]
        }
      ];
    } else {
    const barOptions: SidebarOption[] = [
      {
        id: 'Home',
        label: 'Inicio',
        linkTo: '/priv/inicio',
        module: 'home',
        visible: true,
        icon: 'home'
      },
      {
        id: 'collection-process',
        label: 'Procesos de cobranza',
        module: 'collection_process',
        icon: 'cash',
        subOptions: [
          {
            id: 'my-collection-processes',
            label: 'Mi cartera',
            linkTo: "/priv/procesos-cobranza",
            params: { global_status: 'current', own_processes: 'true' },
            module: 'collection_process',
            icon: 'wallet'
          },
          {
            id: 'global-collection-processes',
            label: 'Cartera global',
            linkTo: "/priv/procesos-cobranza",
            params: { global_status: 'current', own_processes: 'false' },
            module: 'collection_process',
            icon: 'world'
          },
          {
            id: 'payment-review',
            label: 'Historial de pagos',
            linkTo: '/priv/historial-pago',
            params: {reviewed: 'false'},
            module: 'collection_process',
            icon: 'receipt'
          },
          {
            id: 'payment-links',
            label: 'Historial de enlaces de pago',
            linkTo: '/priv/procesos-cobranza/enlaces-de-pago',
            module: 'collection_process',
            icon: 'credit-card'
          },
          {
            id: 'bulk-load',
            label: 'Carga masiva a procesos de cobranza',
            linkTo: '/priv/procesos-cobranza/carga-masiva',
            module: 'collection_process',
            icon: 'file-upload'
          },
          {
            id: 'clients',
            label: 'Clientes',
            linkTo: '/priv/procesos-cobranza/clientes',
            module: 'collection_process',
            icon: 'address-book'
          },
          {
            id: 'client-payments',
            label: 'Pagos en procesos de cobranza',
            linkTo: '/priv/procesos-cobranza/pagos',
            module: 'collection_process',
            icon: 'cash-banknote'
          }
        ]
      },
      {
        id: 'legal-processes',
        label: 'Procesos legales',
        module: 'legal_process',
        icon: 'gavel',
        subOptions: [
          {
            id: 'legal-processes-current',
            label: 'Procesos vigentes',
            linkTo: "/priv/procesos-legales",
            params: { global_status: 'current' },
            module: 'legal_process',
            icon: 'folder-open'
          },
          {
            id: 'legal-processes-current',
            label: 'Procesos cerrados',
            linkTo: '/priv/procesos-legales',
            params: { global_status: 'closed' },
            module: 'legal_process',
            icon: 'folder'
          }
        ]
      },
      {
        id: 'restructuring-process',
        label: 'Restructuraciones',
        module: 'restructuring_process',
        icon: 'file-pencil',
        visible: true,
        subOptions: [
          {
            id: 'restructuring-process-list',
            label: 'Cartera',
            linkTo: '/priv/restructuraciones',
            module: 'restructuring_process',
            icon: 'list',
            visible: true,
          },
          {
            id: 'restructuring-bulk-load',
            label: 'Carga masiva a restructuraciones',
            linkTo: '/priv/restructuraciones/carga-masiva',
            module: 'restructuring_process',
            icon: 'file-upload',
            visible: true,
          }
        ]
      },
      {
        id: 'payment-provision',
        label: 'Provisiones de pago',
        module: 'payment_provision',
        icon: 'calendar',
        visible: true,
        subOptions: [
          {
            id: 'credit-payment-provision',
            label: 'Crear provision de pago',
            linkTo: '/priv/provisiones-pago/crear',
            module: 'payment_provision',
            icon: 'plus',
            visible: true,
          },
          {
            id: 'payment-provision-list',
            label: 'Ver provisiones',
            linkTo: '/priv/provisiones-pago/listado',
            module: 'payment_provision',
            icon: 'list',
            visible: true,
          }
        ]
      },
      {
        id: 'financial',
        label: 'Financiero',
        module: 'payment_cycles',
        icon: 'coin',
        visible: true,
        subOptions: [
          {
            id: 'payment-cycles',
            label: 'Ciclos de pago',
            linkTo: '/priv/financiero/ciclos-de-pago',
            module: 'payment_cycles',
            icon: 'calendar',
            visible: true,
          }
        ]
      },
      {
        id: 'authorization-history',
        label: 'Autorizaciones',
        linkTo: '/priv/historial-autorizaciones',
        module: 'authorization_history',
        icon: 'shield-check',
        visible: true,
      },
      {
        id: 'legal-coverage',
        label: 'Cobertura jurídica',
        linkTo: '/priv/cobertura-juridica',
        module: 'legal_coverage_process',
        icon: 'shield'
      },
      {
        id: 'collection-house',
        label: 'Casa de cobranza',
        module: 'collection_house',
        icon: 'building',
        subOptions: [
          {
            id: 'collection-house-current',
            label: 'Procesos de cartera',
            linkTo: "/priv/casa-cobranza",
            params: { global_status: 'current' },
            module: 'collection_house',
            icon: 'folder-open'
          }
        ]
      },
      {
        id: 'payment-commitments',
        label: 'Compromisos de pago',
        linkTo: '/priv/compromisos-pago/lista',
        module: 'payment_commitment',
        icon: 'calendar',
      },
      {
        id: 'alerts',
        label: 'Alertas',
        linkTo: '/priv/alertas',
        params: { availability: 'pending' },
        module: 'alarms',
        icon: 'bell'
      },
      {
        id: 'debtors',
        label: 'Deudores',
        linkTo: '/priv/deudores',
        module: 'debtors',
        icon: 'moneybag'
      },
      {
        id: 'payment-agreements',
        label: 'Acuerdos de pago',
        linkTo: '/priv/acuerdos-pago',
        module: 'payment_agreement',
        icon: 'handshake',
      },
      {
        id: 'notifications',
        label: 'Notificaciones',
        module: 'notifications',
        icon: 'mail',
        subOptions: [
          {
            id: 'notifications-list',
            label: 'Histórico de notificaciones',
            linkTo: '/priv/notificaciones',
            params: { medium: 'email' },
            module: 'notifications',
            icon: 'history'
          },
          {
            id: 'notifications-create',
            label: 'Crear notificación',
            linkTo: "/priv/notificaciones/crear/correo",
            module: 'notifications',
            icon: 'mail-opened'
          },
          {
            id: 'bulk-sms-messaging',
            label: 'Envío masivo de SMS',
            linkTo: "/priv/notificaciones/envio-masivo-sms",
            module: 'notifications',
            icon: 'message-circle'
          }
        ]
      },
      {
        id: 'notifications-history',
        label: 'Historial de correos',
        linkTo: '/priv/notificaciones/historial',
        module: 'email_tracking',
        icon: 'list',
        visible: true,
      },
      {
        id: 'financial-indicators',
        label: 'Indicadores financieros',
        module: 'financial_indicators',
        icon: 'chart-column',
        subOptions: [
          {
            id: 'rate-list',
            label: 'Tasas',
            linkTo: '/priv/indicadores-financieros/tasas',
            module: 'financial_indicators',
            icon: 'percentage',
          },
          {
            id: 'gac-list',
            label: 'GAC',
            linkTo: '/priv/indicadores-financieros',
            module: 'financial_indicators',
            icon: 'currency-dollar',
          }
        ]
      },
      {
        id: 'generate-reports',
        label: 'Reportes',
        module: 'generation_of_reports',
        icon: 'list-check',
        linkTo: '/priv/generar-reportes'
      },
      {
        id: 'database-query',
        label: 'Consultas en bases de datos',
        module: 'database_query',
        icon: 'stack-2',
        linkTo: '/priv/consultas-bases-datos/historial'
      },
    ];

    if (this._sessionService.user?.type === 'admin') {
      barOptions.splice(1, 0, {
        id: 'Indicators',
        label: 'Gestión de cobranza',
        linkTo: `/priv/gestion-cobranza/indicadores/generales`,
        params: { year: `${new Date().getFullYear()}`, month: `${new Date().getMonth() + 1}` },
        module: 'indicators',
        visible: false,
        icon: 'chart-bar'
      },
      {
        id: 'collection_score',
        label: 'Score de cobranza',
        module: 'collection_score',
        icon: 'chart-histogram',
        subOptions: [
          {
            id: 'variables',
            label: 'Variables',
            linkTo: "/priv/score-cobranza/variables",
            module: 'collection_score',
            icon: 'adjustments-horizontal'
          },
          {
            id: 'score',
            label: 'Score',
            linkTo: "/priv/score-cobranza/score",
            module: 'collection_score',
            icon: 'chart-line'
          }
        ]
      });
    }
    return barOptions;
    }
  }
}
